"use client";

import { Card } from "@components";
import Link from "next/link";
import VadImage from "vadimages-nextjs-image-optimizer";
import linkCss from "@/parts/ProductCard/product-card.module.scss";
import { useTranslations } from "@/app/(site)/europe/useTranslations";

/**
 * Unlike the tiles around it this card drops the muted tan and paints the render's own backdrop
 * instead: a near-flat vertical ramp, #DFDFDF at the top of the frame down to #D5D5D5 at the
 * bottom, sampled off the frame's right edge, with the floor settling around #D0D0D0. The render
 * covers the left of the card and the card carries that ramp on to the right, so the tile reads
 * as one photo with the copy laid over it rather than a picture butted against a differently
 * coloured panel. Two things keep the join invisible, so preserve both if you touch this:
 *
 *  - The ramp has to span the same height as the frame, which on desktop it does because the
 *    render is the taller of the two things sharing its grid cell. Let the copy outgrow it and
 *    the ramp stretches past the frame's bottom edge, which is where the join starts to show.
 *  - The colours are baked into the class strings on purpose. Tailwind only generates arbitrary
 *    values it can read literally in the source, so building them from constants would leave the
 *    card with no background at all.
 */
export const SingularCard = () => {
  const { singularHeadline, singularBody, viewLink } = useTranslations();

  // Leads the stocked-lineup section, so the spacing that used to sit above the card now sits
  // below it, matching the gap ProductGroup keeps between the category blocks that follow.
  return (
    <Card
      bgColor=""
      className="relative w-full mb-16 bg-[#D0D0D0] md:grid md:grid-cols-1 md:bg-[linear-gradient(to_bottom,#DFDFDF,#D5D5D5)]"
    >
      <div className="relative md:w-[78%] md:[grid-area:1/1]">
        <VadImage
          src="/images/singular/singular-eu-lineup.jpg"
          alt="The Singular pod range alongside a Singular battery"
          width={1280}
          height={720}
          className="w-full h-auto"
        />
        {/*
          Mobile stacks the copy under the render, so the frame's bottom edge would otherwise cut
          a line across the card — its floor is a shade darker than the backdrop behind the copy.
          This fades one into the other. Desktop needs none of it: the copy sits on the render.
        */}
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-[#D0D0D0] md:hidden" />
      </div>

      {/*
        Laid over the render by sharing its grid cell rather than by absolute positioning, so the
        card takes the taller of the two. The render wins at every width the lander is read at, so
        this normally changes nothing — it just means a longer translation pushes the card down
        instead of being clipped by the card's overflow. Indented past 55% of the card, which
        clears the pods at any height: they run out at 65% of the frame, so 51% of the card.

        `relative` is load-bearing, not leftover: the render's wrapper is positioned, so a static
        copy block loses the paint order to it and everything left of the render's edge disappears
        behind the picture. Positioning the copy too puts it back on top, later sibling winning.
      */}
      <div className="relative hidden md:flex md:flex-col md:justify-center md:[grid-area:1/1] md:pl-[55%] md:pr-[4%] space-y-3.5">
        <div>
          <span className="bg-[#F12C01] rounded-full text-white text-[13px] font-semibold px-6 py-2">
            NEW
          </span>
        </div>
        <h5 className="font-semibold">{singularHeadline}</h5>
        <p>{singularBody}</p>
        <div>
          <Link href="/singular" target="_blank" className={linkCss["link"]}>
            {viewLink}
          </Link>
        </div>
      </div>

      {/*
        Badge on the render, as before, but on the right: this frame is only clear of the pods for
        the top 9% of its height, which is shorter than the badge, whereas its whole right side is
        empty backdrop.
      */}
      <div className="absolute top-4 right-4 md:hidden">
        <span className="bg-[#F12C01] rounded-full text-white text-[13px] font-semibold px-6 py-2">
          NEW
        </span>
      </div>
      <div className="md:hidden text-center px-4 pb-10 space-y-3.5">
        <h5 className="font-semibold">{singularHeadline}</h5>
        <p className="max-w-[446px] mx-auto">{singularBody}</p>
        <div>
          <Link href="/singular" target="_blank" className={linkCss["link"]}>
            {viewLink}
          </Link>
        </div>
      </div>
    </Card>
  );
};
